import React, { useCallback, useEffect, useMemo, useState } from "react";
import QRCode from "react-qr-code";
import {
  Link,
  Navigate,
  NavLink,
  useLocation,
  useNavigate,
} from "react-router-dom";
import PasswordField from "../common/PasswordField";
import { useAuth } from "../context/AuthContext";
import mozzoCollaboratorDebugApkUrl from "../../android/mozzo-kiosk/collaborator-app/build/outputs/apk/debug/collaborator-app-debug.apk";
import mozzoCollaboratorReleaseApkUrl from "../../android/mozzo-kiosk/collaborator-app/build/outputs/apk/release/collaborator-app-release.apk";
import { MOZZOAPP_INSTITUTIONAL_LOGO_URL } from "../../services/local/institutionalBranding";
import {
  COLLABORATOR_PROGRAM_CHANGED_EVENT,
  COLLABORATOR_CONTRACT_VERSION,
  COLLABORATOR_PROGRAM_RULES,
  COLLABORATOR_PROGRAM_STORAGE_KEY,
  acceptLocalCollaboratorContract,
  clearLocalCollaboratorSession,
  createEmptyCollaboratorProgramState,
  createLocalCollaboratorInvitation,
  createLocalDemoSession,
  formatProgramRate,
  getCollaboratorProfile,
  hasAcceptedCurrentCollaboratorContract,
  isCollaboratorProgramDemoMode,
  isOperationalCollaboratorLocation,
  linkLocalRestaurant,
  loadLocalCollaboratorProgramState,
  loadLocalCollaboratorSession,
  mergeCollaboratorAccessState,
  registerLocalCollaborator,
  registerLocalSettlementInvoice,
  resetLocalCollaboratorProgram,
  resolveStarBonusPortfolio,
  resolveStarNetworkBonus,
  updateLocalCollaboratorProfile,
} from "../../services/local/collaboratorProgram";
import {
  acceptCollaboratorAccessInvitation,
  createCollaboratorAccessInvitation,
  findCollaboratorProfileForUser,
  resumeCurrentCollaboratorAccess,
  updateCollaboratorAccessProfile,
} from "../../services/supabase/collaboratorAccessService";
import {
  createCollaboratorLocationReferral,
  loadCollaboratorProgramState,
  registerCollaboratorSettlementInvoice,
} from "../../services/supabase/collaboratorProgramService";
import { updateCurrentUserPassword } from "../../services/supabase/authService";
import { isMissingAuthSessionError, MISSING_AUTH_SESSION_MESSAGE } from "../../services/supabase/authErrors";
import "../../styles/Colaboradores.css";

const moneyFormatter = new Intl.NumberFormat("es-AR", {
  style: "currency",
  currency: "ARS",
  maximumFractionDigits: 0,
});

const dateFormatter = new Intl.DateTimeFormat("es-AR", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  timeZone: "America/Argentina/Buenos_Aires",
});

const formatMoney = (value) => moneyFormatter.format(Number(value || 0));

const formatDate = (value) => {
  if (!value) {
    return "Sin fecha";
  }

  const parsed = new Date(`${String(value).slice(0, 10)}T12:00:00-03:00`);
  return Number.isNaN(parsed.getTime()) ? String(value) : dateFormatter.format(parsed);
};

const LOCATION_STATUS_META = {
  active: { label: "Operativa", tone: "success" },
  fee_pending: { label: "Comision pendiente", tone: "warning" },
  prelinked: { label: "Esperando registro", tone: "info" },
  cancelled: { label: "Baja", tone: "neutral" },
};

const SETTLEMENT_STATUS_META = {
  awaiting_invoice: { label: "Falta factura", tone: "warning" },
  invoice_submitted: { label: "Factura recibida", tone: "info" },
  invoice_late: { label: "Proximo pago", tone: "warning" },
  scheduled: { label: "Pago programado", tone: "info" },
  paid: { label: "Pagada", tone: "success" },
};

const ANDROID_COLLABORATOR_APK_FILE_NAMES = {
  beta: "MozzoColaboradores-Beta.apk",
  production: "MozzoColaboradores.apk",
};

const useCollaboratorLauncherConfig = () =>
  useMemo(() => {
    const userAgent =
      typeof navigator === "undefined" ? "" : String(navigator.userAgent || "");
    const hostname =
      typeof window === "undefined"
        ? ""
        : String(window.location.hostname || "").toLowerCase();
    const isIosDevice =
      /iPad|iPhone|iPod/i.test(userAgent) ||
      (typeof navigator !== "undefined" &&
        navigator.platform === "MacIntel" &&
        navigator.maxTouchPoints > 1);
    const isProduction =
      hostname === "mozzoapp.com" || hostname === "www.mozzoapp.com";
    const selectedApkUrl = isProduction
      ? mozzoCollaboratorReleaseApkUrl
      : mozzoCollaboratorDebugApkUrl;

    return {
      isAndroidLauncher: /MozzoCollaborator\/[\d.]+/i.test(userAgent),
      isAndroidDevice: /Android/i.test(userAgent),
      isIosDevice,
      isStandaloneApp: Boolean(
        typeof window !== "undefined" &&
          (window.matchMedia?.("(display-mode: standalone)")?.matches ||
            window.navigator?.standalone)
      ),
      apkUrl:
        selectedApkUrl && typeof window !== "undefined"
          ? new URL(selectedApkUrl, window.location.origin).toString()
          : selectedApkUrl || "",
      apkFileName: isProduction
        ? ANDROID_COLLABORATOR_APK_FILE_NAMES.production
        : ANDROID_COLLABORATOR_APK_FILE_NAMES.beta,
      iosInstallUrl:
        typeof window !== "undefined"
          ? new URL("/colaboradores", window.location.origin).toString()
          : "https://mozzoapp.com/colaboradores",
    };
  }, []);

const getLocationStatusMeta = (location, settings) => {
  if (
    location?.status === "active" &&
    !isOperationalCollaboratorLocation(location, settings)
  ) {
    return { label: "Debajo del minimo", tone: "neutral" };
  }

  return LOCATION_STATUS_META[location?.status] || LOCATION_STATUS_META.cancelled;
};

function StatusBadge({ meta }) {
  return (
    <span className={`collab-status collab-status--${meta?.tone || "neutral"}`}>
      {meta?.label || "Sin estado"}
    </span>
  );
}

function CopyButton({ value, children = "Copiar" }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(value || ""));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  return (
    <button type="button" className="collab-button collab-button--quiet" onClick={handleCopy}>
      {copied ? "Copiado" : children}
    </button>
  );
}

function Modal({ title, description, onClose, children, width = "640px" }) {
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div className="collab-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="collab-modal"
        style={{ maxWidth: width }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="collab-modal-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="collab-modal__header">
          <div>
            <h2 id="collab-modal-title">{title}</h2>
            {description ? <p>{description}</p> : null}
          </div>
          <button
            type="button"
            className="collab-icon-button"
            onClick={onClose}
            aria-label="Cerrar"
            title="Cerrar"
          >
            &times;
          </button>
        </header>
        <div className="collab-modal__body">{children}</div>
      </section>
    </div>
  );
}

function CollaboratorLauncherControl({ launcherConfig }) {
  const [selectedPlatform, setSelectedPlatform] = useState("");

  if (launcherConfig.isAndroidLauncher || launcherConfig.isStandaloneApp) {
    return null;
  }

  return (
    <>
      <div className="collab-launcher-actions">
        {!launcherConfig.isIosDevice ? (
          <button
            type="button"
            className="collab-button collab-button--quiet collab-launcher-trigger"
            onClick={() => setSelectedPlatform("android")}
          >
            <span aria-hidden="true">↓</span>
            Descargar Android
          </button>
        ) : null}
        {!launcherConfig.isAndroidDevice ? (
          <button
            type="button"
            className="collab-button collab-button--quiet collab-launcher-trigger"
            onClick={() => setSelectedPlatform("ios")}
          >
            <span aria-hidden="true">＋</span>
            Instalar en iPhone
          </button>
        ) : null}
      </div>

      {selectedPlatform === "android" ? (
        <Modal
          title="Mozzo Colaboradores para Android"
          description="Aplicacion movil del portal de colaboradores."
          width="720px"
          onClose={() => setSelectedPlatform("")}
        >
          <div className="collab-launcher-content">
            <div className="collab-launcher-download">
              <div className="collab-launcher-qr">
                {launcherConfig.apkUrl ? (
                  <QRCode
                    value={launcherConfig.apkUrl}
                    size={188}
                    bgColor="#ffffff"
                    fgColor="#182230"
                  />
                ) : null}
              </div>
              <a
                className={`collab-button collab-button--primary collab-button--block ${
                  launcherConfig.apkUrl ? "" : "is-disabled"
                }`}
                href={launcherConfig.apkUrl || undefined}
                download={launcherConfig.apkFileName}
                aria-disabled={!launcherConfig.apkUrl}
                onClick={(event) => {
                  if (!launcherConfig.apkUrl) {
                    event.preventDefault();
                  }
                }}
              >
                Descargar APK
              </a>
              <small>{launcherConfig.apkFileName}</small>
            </div>

            <div className="collab-launcher-instructions">
              <span className="collab-section-label">Instalacion en el celular</span>
              <h3>Mozzo Colaboradores</h3>
              <ol>
                <li>Escanea el QR desde el dispositivo Android o descarga el APK.</li>
                <li>Autoriza la instalacion si Android solicita permiso.</li>
                <li>Abre Mozzo Colaboradores e ingresa con tu correo.</li>
                <li>La sesion quedara guardada en la aplicacion.</li>
              </ol>
              <div className="collab-launcher-note">
                El launcher abre solamente el portal comercial y permite adjuntar la
                factura de cada liquidacion.
              </div>
            </div>
          </div>
        </Modal>
      ) : null}

      {selectedPlatform === "ios" ? (
        <Modal
          title="Mozzo Colaboradores para iPhone"
          description="Instala el portal como una app desde Safari."
          width="720px"
          onClose={() => setSelectedPlatform("")}
        >
          <div className="collab-launcher-content">
            <div className="collab-launcher-download">
              <div className="collab-launcher-qr">
                <QRCode
                  value={launcherConfig.iosInstallUrl}
                  size={188}
                  bgColor="#ffffff"
                  fgColor="#182230"
                />
              </div>
              <a
                className="collab-button collab-button--primary collab-button--block"
                href={launcherConfig.iosInstallUrl}
              >
                Abrir portal en Safari
              </a>
              <small>mozzoapp.com/colaboradores</small>
            </div>

            <div className="collab-launcher-instructions">
              <span className="collab-section-label">Instalacion en iPhone</span>
              <h3>Mozzo Colaboradores</h3>
              <ol>
                <li>Abre este portal con Safari en el iPhone.</li>
                <li>Toca el boton Compartir de Safari.</li>
                <li>Selecciona Agregar a pantalla de inicio.</li>
                <li>Confirma con Agregar.</li>
              </ol>
              <div className="collab-launcher-note">
                Se instalara con el nombre Mozzo Colaboradores, abrira directamente
                el portal comercial y conservara la sesion del colaborador.
              </div>
            </div>
          </div>
        </Modal>
      ) : null}
    </>
  );
}

function CollaboratorAccess({
  mode,
  programState,
  launcherConfig,
  demoMode,
  externalError = "",
  onLogin,
  onOpenDemo,
  onRegister,
}) {
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({
    invitationCode: query.get("code") || "",
    email: query.get("email") || "",
    firstName: "",
    lastName: "",
    phone: "",
    cuit: "",
    payoutAlias: "",
    password: "",
    confirmPassword: "",
    acceptsTaxRequirement: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!loginForm.email.trim() || loginForm.password.length < 6) {
      setError("Completa el correo y una contrasena de al menos 6 caracteres.");
      return;
    }

    setSubmitting(true);
    try {
      await onLogin(loginForm);
    } catch (submitError) {
      setError(submitError.message || "No pudimos abrir el portal.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegisterSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!registerForm.acceptsTaxRequirement) {
      setError("Confirma que podras presentar un comprobante fiscal valido.");
      return;
    }

    if (registerForm.password.length < 6) {
      setError("La contrasena debe tener al menos 6 caracteres.");
      return;
    }

    if (registerForm.password !== registerForm.confirmPassword) {
      setError("Las contrasenas no coinciden.");
      return;
    }

    setSubmitting(true);
    try {
      await onRegister(registerForm);
    } catch (submitError) {
      setError(submitError.message || "No pudimos crear el acceso.");
    } finally {
      setSubmitting(false);
    }
  };

  const fillDemoInvitation = () => {
    const invitation = programState.invitations.find(
      (candidate) => candidate.status === "pending"
    );

    if (!invitation) {
      setError("No quedan invitaciones demo pendientes. Reinicia los datos locales desde el panel.");
      return;
    }

    const [firstName = "", ...lastNameParts] = invitation.name.split(" ");
    setRegisterForm((current) => ({
      ...current,
      invitationCode: invitation.code,
      email: invitation.email,
      firstName,
      lastName: lastNameParts.join(" "),
      phone: "+54 9 379 400 2200",
      cuit: "27-34567890-1",
      payoutAlias: "paula.mozzo",
      password: "mozzo2026",
      confirmPassword: "mozzo2026",
      acceptsTaxRequirement: true,
    }));
    setError("");
  };

  return (
    <main className="collab-access">
      <header className="collab-access__header">
        <Link to="/login" className="collab-brand" aria-label="Volver a MozzoApp">
          <img src={MOZZOAPP_INSTITUTIONAL_LOGO_URL} alt="" />
          <span>
            <strong>MozzoApp</strong>
            <small>Colaboradores</small>
          </span>
        </Link>
        <div className="collab-access__actions">
          <span className="collab-local-badge">
            {demoMode ? "Vista preliminar" : "Produccion"}
          </span>
          <CollaboratorLauncherControl launcherConfig={launcherConfig} />
        </div>
      </header>

      <div className="collab-access__workspace">
        <section className="collab-access__form-panel">
          <div className="collab-access__heading">
            <span className="collab-kicker">
              {mode === "register" ? "Alta por invitacion" : "Portal comercial"}
            </span>
            <h1>
              {mode === "register"
                ? "Activa tu perfil de colaborador"
                : "Ingresa a tu operacion comercial"}
            </h1>
            <p>
              {mode === "register"
                ? "El correo y el codigo deben coincidir con la invitacion emitida por tu colaborador estrella."
                : "Consulta comercios vinculados, cierres mensuales, facturas y objetivos de red."}
            </p>
          </div>

          <div className="collab-segmented" aria-label="Tipo de acceso">
            <Link
              className={mode === "login" ? "is-active" : ""}
              to="/colaboradores"
            >
              Ingresar
            </Link>
            <Link
              className={mode === "register" ? "is-active" : ""}
              to="/colaboradores/registro"
            >
              Crear acceso
            </Link>
          </div>

          {mode === "register" ? (
            <form className="collab-form" onSubmit={handleRegisterSubmit}>
              <div className="collab-form-grid">
                <label className="collab-field collab-field--wide">
                  <span>Codigo de invitacion</span>
                  <input
                    value={registerForm.invitationCode}
                    onChange={(event) =>
                      setRegisterForm((current) => ({
                        ...current,
                        invitationCode: event.target.value.toUpperCase(),
                      }))
                    }
                    placeholder="MZ-INV-0000"
                    autoComplete="off"
                  />
                </label>
                <label className="collab-field collab-field--wide">
                  <span>Correo invitado</span>
                  <input
                    type="email"
                    value={registerForm.email}
                    onChange={(event) =>
                      setRegisterForm((current) => ({
                        ...current,
                        email: event.target.value,
                      }))
                    }
                    placeholder="colaborador@correo.com"
                    autoComplete="email"
                  />
                </label>
                <label className="collab-field">
                  <span>Nombre</span>
                  <input
                    value={registerForm.firstName}
                    onChange={(event) =>
                      setRegisterForm((current) => ({
                        ...current,
                        firstName: event.target.value,
                      }))
                    }
                    autoComplete="given-name"
                  />
                </label>
                <label className="collab-field">
                  <span>Apellido</span>
                  <input
                    value={registerForm.lastName}
                    onChange={(event) =>
                      setRegisterForm((current) => ({
                        ...current,
                        lastName: event.target.value,
                      }))
                    }
                    autoComplete="family-name"
                  />
                </label>
                <label className="collab-field">
                  <span>CUIT</span>
                  <input
                    value={registerForm.cuit}
                    onChange={(event) =>
                      setRegisterForm((current) => ({
                        ...current,
                        cuit: event.target.value,
                      }))
                    }
                    placeholder="20-00000000-0"
                    inputMode="numeric"
                  />
                </label>
                <label className="collab-field">
                  <span>Telefono</span>
                  <input
                    value={registerForm.phone}
                    onChange={(event) =>
                      setRegisterForm((current) => ({
                        ...current,
                        phone: event.target.value,
                      }))
                    }
                    autoComplete="tel"
                  />
                </label>
                <label className="collab-field collab-field--wide">
                  <span>Alias para liquidaciones</span>
                  <input
                    value={registerForm.payoutAlias}
                    onChange={(event) =>
                      setRegisterForm((current) => ({
                        ...current,
                        payoutAlias: event.target.value,
                      }))
                    }
                    placeholder="nombre.alias"
                    autoComplete="off"
                  />
                </label>
                <div className="collab-field">
                  <PasswordField
                    label="Contrasena"
                    name="collaborator-password"
                    value={registerForm.password}
                    onChange={(event) =>
                      setRegisterForm((current) => ({
                        ...current,
                        password: event.target.value,
                      }))
                    }
                    autoComplete="new-password"
                  />
                </div>
                <div className="collab-field">
                  <PasswordField
                    label="Repetir contrasena"
                    name="collaborator-confirm-password"
                    value={registerForm.confirmPassword}
                    onChange={(event) =>
                      setRegisterForm((current) => ({
                        ...current,
                        confirmPassword: event.target.value,
                      }))
                    }
                    autoComplete="new-password"
                  />
                </div>
              </div>

              <label className="collab-checkbox">
                <input
                  type="checkbox"
                  checked={registerForm.acceptsTaxRequirement}
                  onChange={(event) =>
                    setRegisterForm((current) => ({
                      ...current,
                      acceptsTaxRequirement: event.target.checked,
                    }))
                  }
                />
                <span>Podre emitir un comprobante fiscal valido para cada liquidacion.</span>
              </label>

              {error || externalError ? (
                <div className="collab-message collab-message--error">
                  {error || externalError}
                </div>
              ) : null}

              <div className="collab-form-actions">
                {demoMode ? (
                  <button
                    type="button"
                    className="collab-button collab-button--quiet"
                    onClick={fillDemoInvitation}
                  >
                    Usar invitacion local
                  </button>
                ) : null}
                <button
                  type="submit"
                  className="collab-button collab-button--primary"
                  disabled={submitting}
                >
                  {submitting ? "Creando acceso..." : "Activar perfil"}
                </button>
              </div>
            </form>
          ) : (
            <form className="collab-form" onSubmit={handleLoginSubmit}>
              <label className="collab-field">
                <span>Correo</span>
                <input
                  type="email"
                  value={loginForm.email}
                  onChange={(event) =>
                    setLoginForm((current) => ({
                      ...current,
                      email: event.target.value,
                    }))
                  }
                  placeholder="colaborador@correo.com"
                  autoComplete="email"
                />
              </label>
              <PasswordField
                label="Contrasena"
                name="collaborator-login-password"
                value={loginForm.password}
                onChange={(event) =>
                  setLoginForm((current) => ({
                    ...current,
                    password: event.target.value,
                  }))
                }
                autoComplete="current-password"
              />

              <div className="collab-password-help">
                <Link to="/recuperar-contrasena?returnTo=%2Fcolaboradores">
                  &iquest;Olvidaste tu contrase&ntilde;a?
                </Link>
              </div>

              {error || externalError ? (
                <div className="collab-message collab-message--error">
                  {error || externalError}
                </div>
              ) : null}

              <button
                type="submit"
                className="collab-button collab-button--primary collab-button--block"
                disabled={submitting}
              >
                {submitting ? "Ingresando..." : "Ingresar al portal"}
              </button>
            </form>
          )}
        </section>

        <aside className="collab-access__aside">
          <div>
            <span className="collab-kicker">
              {demoMode ? "Datos de demostracion" : "Programa MozzoApp"}
            </span>
            <h2>
              {demoMode
                ? "Dos perfiles listos para revisar"
                : "Gestion comercial para colaboradores"}
            </h2>
          </div>
          {demoMode ? (
            <div className="collab-demo-list">
              <button type="button" onClick={() => onOpenDemo("star")}>
                <span>
                  <strong>Colaborador estrella</strong>
                  <small>Red, invitaciones y bono activo</small>
                </span>
                <b>96</b>
              </button>
              <button type="button" onClick={() => onOpenDemo("normal")}>
                <span>
                  <strong>Colaborador normal</strong>
                  <small>Comercios, facturas y progreso a 30</small>
                </span>
                <b>2</b>
              </button>
            </div>
          ) : null}
          <dl className="collab-access__rules">
            <div>
              <dt>Minimo operativo</dt>
              <dd>{formatMoney(programState.programSettings.minimumCommissionableSales)}</dd>
            </div>
            <div>
              <dt>Cierre</dt>
              <dd>Ultimo dia del mes</dd>
            </div>
            <div>
              <dt>Factura</dt>
              <dd>Hasta el dia 10</dd>
            </div>
            <div>
              <dt>Pago</dt>
              <dd>Hasta el dia 15</dd>
            </div>
          </dl>
        </aside>
      </div>
    </main>
  );
}

function MetricStrip({ metrics }) {
  return (
    <div className="collab-metric-strip">
      {metrics.map((metric) => (
        <article key={metric.label} className="collab-metric">
          <span>{metric.label}</span>
          <strong>{metric.value}</strong>
          <small className={metric.tone ? `is-${metric.tone}` : ""}>{metric.detail}</small>
        </article>
      ))}
    </div>
  );
}

function ProgressBar({ value, max = 100, label }) {
  const percentage = Math.max(0, Math.min(100, (Number(value || 0) / max) * 100));

  return (
    <div className="collab-progress" aria-label={label}>
      <span style={{ width: `${percentage}%` }} />
    </div>
  );
}

function LocationTable({ locations, settings, compact = false }) {
  if (!locations.length) {
    return <div className="collab-empty">Todavia no hay comercios vinculados.</div>;
  }

  return (
    <div className="collab-table-wrap">
      <table className={`collab-table ${compact ? "collab-table--compact" : ""}`}>
        <thead>
          <tr>
            <th>Comercio</th>
            <th>Tipo</th>
            <th>Venta comisionable</th>
            <th>Tu tasa</th>
            <th>Estado</th>
          </tr>
        </thead>
        <tbody>
          {locations.map((location) => (
            <tr key={location.id}>
              <td>
                <strong>{location.companyName}</strong>
                <small>{location.branchName}</small>
              </td>
              <td>{location.locationType === "branch" ? "Sucursal" : "Principal"}</td>
              <td>{formatMoney(location.commissionableSales)}</td>
              <td>{formatProgramRate(location.collaboratorRate)}</td>
              <td>
                <StatusBadge meta={getLocationStatusMeta(location, settings)} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CollaboratorContractGate({
  profile,
  programState,
  onAccept,
  onLogout,
}) {
  const [form, setForm] = useState({
    firstName: profile.firstName || "",
    lastName: profile.lastName || "",
    cuit: profile.cuit || "",
    phone: profile.phone || "",
    taxCondition: profile.taxCondition || "",
    fiscalAddress: profile.fiscalAddress || "",
    payoutAlias: profile.payoutAlias || "",
    acceptsContract: false,
    acceptsIndependentRelationship: false,
    acceptsInvoiceRequirement: false,
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      const nextState = acceptLocalCollaboratorContract(
        programState,
        profile.id,
        form
      );
      await onAccept(nextState);
    } catch (submitError) {
      setError(submitError.message || "No pudimos registrar la aceptacion.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="collab-gate">
      <header className="collab-access__header">
        <Link to="/login" className="collab-brand" aria-label="Volver a MozzoApp">
          <img src={MOZZOAPP_INSTITUTIONAL_LOGO_URL} alt="" />
          <span>
            <strong>MozzoApp</strong>
            <small>Contrato digital</small>
          </span>
        </Link>
        <button
          type="button"
          className="collab-button collab-button--quiet"
          onClick={onLogout}
        >
          Salir
        </button>
      </header>

      <section className="collab-gate__shell">
        <div className="collab-gate__intro">
          <span className="collab-kicker">Paso obligatorio</span>
          <h1>Completa tus datos y acepta el contrato</h1>
          <p>
            Antes de usar el portal comercial necesitamos dejar registrada la
            identidad fiscal, la cuenta de cobro y la aceptacion digital del
            contrato vigente para colaboradores MozzoApp.
          </p>
        </div>

        <form className="collab-gate__grid" onSubmit={handleSubmit}>
          <section className="collab-surface collab-gate__form">
            <div className="collab-section-heading">
              <div>
                <span className="collab-section-label">Datos del colaborador</span>
                <h2>Identidad y facturacion</h2>
              </div>
              <StatusBadge
                meta={{
                  label: profile.role === "star" ? "Estrella" : "Colaborador",
                  tone: profile.role === "star" ? "star" : "info",
                }}
              />
            </div>

            <div className="collab-form-grid">
              <label className="collab-field">
                <span>Nombre</span>
                <input
                  value={form.firstName}
                  onChange={(event) => updateField("firstName", event.target.value)}
                  autoComplete="given-name"
                  required
                />
              </label>
              <label className="collab-field">
                <span>Apellido</span>
                <input
                  value={form.lastName}
                  onChange={(event) => updateField("lastName", event.target.value)}
                  autoComplete="family-name"
                  required
                />
              </label>
              <label className="collab-field collab-field--wide">
                <span>Correo registrado</span>
                <input value={profile.email} readOnly />
              </label>
              <label className="collab-field">
                <span>CUIT / CUIL</span>
                <input
                  value={form.cuit}
                  onChange={(event) => updateField("cuit", event.target.value)}
                  placeholder="20-00000000-0"
                  inputMode="numeric"
                  required
                />
              </label>
              <label className="collab-field">
                <span>Telefono</span>
                <input
                  value={form.phone}
                  onChange={(event) => updateField("phone", event.target.value)}
                  autoComplete="tel"
                  required
                />
              </label>
              <label className="collab-field">
                <span>Condicion fiscal</span>
                <select
                  value={form.taxCondition}
                  onChange={(event) =>
                    updateField("taxCondition", event.target.value)
                  }
                  required
                >
                  <option value="">Seleccionar</option>
                  <option value="monotributo">Monotributista</option>
                  <option value="responsable_inscripto">
                    Responsable inscripto
                  </option>
                  <option value="otra_categoria">Otra categoria habilitada</option>
                </select>
              </label>
              <label className="collab-field">
                <span>Alias / CVU / CBU</span>
                <input
                  value={form.payoutAlias}
                  onChange={(event) =>
                    updateField("payoutAlias", event.target.value)
                  }
                  placeholder="alias.de.cobro"
                  autoComplete="off"
                  required
                />
              </label>
              <label className="collab-field collab-field--wide">
                <span>Domicilio fiscal</span>
                <input
                  value={form.fiscalAddress}
                  onChange={(event) =>
                    updateField("fiscalAddress", event.target.value)
                  }
                  placeholder="Calle, numero, localidad y provincia"
                  autoComplete="street-address"
                  required
                />
              </label>
            </div>
          </section>

          <section className="collab-surface collab-gate__contract">
            <div className="collab-section-heading">
              <div>
                <span className="collab-section-label">
                  Version {COLLABORATOR_CONTRACT_VERSION}
                </span>
                <h2>Contrato de colaboracion comercial</h2>
              </div>
            </div>

            <div className="collab-contract-scroll" tabIndex={0}>
              <h3>Resumen vinculante</h3>
              <p>
                El colaborador actua de forma independiente. No existe relacion
                laboral, sueldo fijo, horario, exclusividad, sociedad, franquicia
                ni representacion legal de MozzoApp.
              </p>
              <p>
                Para cobrar comisiones debe estar registrado ante ARCA como
                monotributista, responsable inscripto u otra categoria valida, y
                debe emitir factura o comprobante fiscal valido a favor de
                MozzoApp.
              </p>
              <p>
                El colaborador comun cobra 0,20% sobre el restaurante principal
                incorporado directamente y 0,10% sobre sus sucursales posteriores,
                siempre que cada unidad supere la facturacion minima vigente y
                MozzoApp haya cobrado efectivamente.
              </p>
              <p>
                Si una sucursal factura menos de{" "}
                {formatMoney(programState.programSettings.minimumCommissionableSales)}
                {" "}en el ciclo mensual, no genera comision y no cuenta como
                operativa para objetivos de estrella. Este minimo puede ajustarse
                por inflacion o actualizacion comercial.
              </p>
              <p>
                Al llegar a 30 restaurantes o sucursales computables, el
                colaborador puede acceder a la categoria estrella y crear su red
                de colaboradores, sujeto a validacion de MozzoApp.
              </p>
              <p>
                El colaborador estrella conserva las comisiones directas de sus
                altas: 0,20% por el restaurante principal y 0,10% por sus
                sucursales posteriores. Por los restaurantes principales que
                incorporen sus colaboradores cobra 0,30% durante tres ciclos,
                desde el segundo ciclo activo.
              </p>
              <p>
                Cuando la cartera computable llega a 100 restaurantes o sucursales,
                el colaborador estrella cobra 10% de la recaudacion neta que
                MozzoApp cobre de esa cartera. Si baja a 99 cobra 9%, a 98 cobra 8%,
                y asi sucesivamente hasta 91. Con 90 o menos no cobra bono de red.
              </p>
              <p>
                Los colaboradores estrella incorporados directamente por MozzoApp,
                sin una estrella superior, tambien suman sus restaurantes y
                sucursales propios al objetivo de 100. Los colaboradores promovidos
                a estrella comienzan su objetivo con las nuevas incorporaciones de
                su propia red.
              </p>
              <p>
                No generan comision los creditos MZ gratuitos, bonificaciones,
                periodos de prueba, pagos rechazados, anulados, contracargos,
                deuda no cobrada, restaurantes bloqueados o importes no
                acreditados a MozzoApp.
              </p>
              <p>
                La aceptacion digital queda registrada con usuario, fecha, hora,
                version, navegador, dispositivo y demas datos tecnicos
                disponibles para acreditar la conformidad.
              </p>
            </div>

            <div className="collab-contract-checks">
              <label className="collab-checkbox">
                <input
                  type="checkbox"
                  checked={form.acceptsContract}
                  onChange={(event) =>
                    updateField("acceptsContract", event.target.checked)
                  }
                />
                <span>
                  Lei y acepto el Contrato Digital de Colaborador Comercial
                  MozzoApp.
                </span>
              </label>
              <label className="collab-checkbox">
                <input
                  type="checkbox"
                  checked={form.acceptsIndependentRelationship}
                  onChange={(event) =>
                    updateField(
                      "acceptsIndependentRelationship",
                      event.target.checked
                    )
                  }
                />
                <span>
                  Entiendo que actuo como colaborador independiente y que no
                  existe relacion laboral con MozzoApp.
                </span>
              </label>
              <label className="collab-checkbox">
                <input
                  type="checkbox"
                  checked={form.acceptsInvoiceRequirement}
                  onChange={(event) =>
                    updateField(
                      "acceptsInvoiceRequirement",
                      event.target.checked
                    )
                  }
                />
                <span>
                  Entiendo que debo estar registrado en ARCA y emitir factura
                  valida para cobrar mis comisiones.
                </span>
              </label>
            </div>

            {error ? (
              <div className="collab-message collab-message--error">{error}</div>
            ) : null}

            <div className="collab-form-actions">
              <button
                type="submit"
                className="collab-button collab-button--primary"
                disabled={submitting}
              >
                {submitting
                  ? "Guardando aceptacion..."
                  : "Aceptar contrato y activar cuenta"}
              </button>
            </div>
          </section>
        </form>
      </section>
    </main>
  );
}

function DashboardView({ profile, programState, navigate }) {
  const isStar = profile.role === "star";
  const locations = programState.locations.filter(
    (location) => location.ownerProfileId === profile.id
  );
  const operationalLocations = locations.filter((location) =>
    isOperationalCollaboratorLocation(location, programState.programSettings)
  );
  const settlements = programState.settlements.filter(
    (settlement) => settlement.profileId === profile.id
  );
  const currentSettlement = settlements[0] || null;
  const networkMembers = programState.networkMembers.filter(
    (member) => member.starProfileId === profile.id && member.status === "active"
  );
  const networkActiveLocations = networkMembers.reduce(
    (sum, member) => sum + Number(member.activeLocationCount || 0),
    0
  );
  const bonusPortfolio = resolveStarBonusPortfolio({
    profile,
    networkOperationalLocationCount: networkActiveLocations,
    ownOperationalLocationCount: operationalLocations.length,
  });
  const bonusStatus = resolveStarNetworkBonus({
    activeLocationCount: bonusPortfolio.activeLocationCount,
    ...(programState.starNetworkStatus[profile.id] || {}),
  });
  const objectiveValue = isStar
    ? bonusPortfolio.activeLocationCount
    : operationalLocations.length;
  const objectiveTarget = isStar
    ? COLLABORATOR_PROGRAM_RULES.starNetworkLocationTarget
    : COLLABORATOR_PROGRAM_RULES.normalPromotionLocationTarget;
  const pendingInvoice = currentSettlement?.status === "awaiting_invoice";

  const metrics = [
    {
      label: "A liquidar",
      value: formatMoney(currentSettlement?.netAmount),
      detail: currentSettlement?.cycleLabel || "Sin cierre",
    },
    {
      label: "Sucursales operativas",
      value: String(operationalLocations.length),
      detail: `${locations.length} vinculadas`,
    },
    {
      label: isStar ? "Cartera para el bono" : "Progreso a estrella",
      value: isStar
        ? String(bonusPortfolio.activeLocationCount)
        : `${objectiveValue}/${objectiveTarget}`,
      detail: isStar
        ? bonusPortfolio.includesOwnLocations
          ? `${networkActiveLocations} de red + ${operationalLocations.length} propias`
          : `${networkMembers.length} colaboradores`
        : "Sucursales que califican",
    },
    {
      label: "Documentacion",
      value: pendingInvoice ? "Pendiente" : "Al dia",
      detail: pendingInvoice ? "Factura requerida" : "Sin acciones vencidas",
      tone: pendingInvoice ? "warning" : "success",
    },
  ];

  return (
    <>
      <div className="collab-page-heading">
        <div>
          <span className="collab-kicker">Ciclo cerrado</span>
          <h1>Resumen comercial</h1>
          <p>Liquidacion de {currentSettlement?.cycleLabel || "este ciclo"} y estado actual de tu cartera.</p>
        </div>
        <button
          type="button"
          className="collab-button collab-button--primary"
          onClick={() => navigate("/colaboradores/comercios", { state: { openLink: true } })}
        >
          Vincular comercio
        </button>
      </div>

      <MetricStrip metrics={metrics} />

      <section className="collab-surface collab-objective">
        <div className="collab-objective__header">
          <div>
            <span className="collab-section-label">
              {isStar ? "Bono de red" : "Camino a colaborador estrella"}
            </span>
            <h2>
              {objectiveValue} de {objectiveTarget} sucursales operativas
            </h2>
          </div>
          <div className="collab-objective__rate">
            <span>{isStar ? "Bono vigente" : "Progreso"}</span>
            <strong>
              {isStar
                ? formatProgramRate(bonusStatus.rate)
                : `${Math.round((objectiveValue / objectiveTarget) * 100)}%`}
            </strong>
          </div>
        </div>
        <ProgressBar
          value={objectiveValue}
          max={objectiveTarget}
          label={`${objectiveValue} de ${objectiveTarget}`}
        />
        <div className="collab-objective__footer">
          <span>
            Cada sucursal debe superar {formatMoney(programState.programSettings.minimumCommissionableSales)} y pagar comision durante el ciclo.
          </span>
          {isStar ? <button type="button" onClick={() => navigate("/colaboradores/red")}>Ver red</button> : null}
        </div>
      </section>

      <div className="collab-two-columns">
        <section className="collab-surface">
          <div className="collab-section-heading">
            <div>
              <span className="collab-section-label">Liquidacion actual</span>
              <h2>{currentSettlement?.cycleLabel || "Sin liquidacion"}</h2>
            </div>
            {currentSettlement ? (
              <StatusBadge meta={SETTLEMENT_STATUS_META[currentSettlement.status]} />
            ) : null}
          </div>
          {currentSettlement ? (
            <dl className="collab-money-breakdown">
              <div>
                <dt>Comision directa</dt>
                <dd>{formatMoney(currentSettlement.directCommission)}</dd>
              </div>
              {profile.role === "star" ? (
                <>
                  <div>
                    <dt>Comision temporal de red</dt>
                    <dd>{formatMoney(currentSettlement.starTemporaryCommission)}</dd>
                  </div>
                  <div>
                    <dt>Bono estrella</dt>
                    <dd>{formatMoney(currentSettlement.networkBonus)}</dd>
                  </div>
                </>
              ) : null}
              <div>
                <dt>Notas de credito</dt>
                <dd className="is-negative">{formatMoney(currentSettlement.creditNotes)}</dd>
              </div>
              <div className="is-total">
                <dt>Total a facturar</dt>
                <dd>{formatMoney(currentSettlement.netAmount)}</dd>
              </div>
            </dl>
          ) : null}
          <button
            type="button"
            className="collab-button collab-button--secondary collab-button--block"
            onClick={() => navigate("/colaboradores/liquidaciones")}
          >
            Revisar liquidacion
          </button>
        </section>

        <section className="collab-surface">
          <div className="collab-section-heading">
            <div>
              <span className="collab-section-label">Fechas del ciclo</span>
              <h2>Calendario de pago</h2>
            </div>
          </div>
          <ol className="collab-timeline">
            <li className="is-done">
              <span>05</span>
              <div><strong>Liquidacion emitida</strong><small>{formatDate(currentSettlement?.statementDate)}</small></div>
            </li>
            <li className={pendingInvoice ? "is-current" : "is-done"}>
              <span>10</span>
              <div><strong>Factura del colaborador</strong><small>Hasta {formatDate(currentSettlement?.invoiceDueDate)}</small></div>
            </li>
            <li>
              <span>15</span>
              <div><strong>Transferencia al alias</strong><small>Hasta {formatDate(currentSettlement?.paymentDueDate)}</small></div>
            </li>
          </ol>
        </section>
      </div>

      <section className="collab-surface collab-surface--table">
        <div className="collab-section-heading collab-section-heading--padded">
          <div>
            <span className="collab-section-label">Cartera personal</span>
            <h2>Ultimos comercios</h2>
          </div>
          <button type="button" className="collab-text-button" onClick={() => navigate("/colaboradores/comercios")}>Ver todos</button>
        </div>
        <LocationTable
          locations={locations.slice(0, 4)}
          settings={programState.programSettings}
          compact
        />
      </section>
    </>
  );
}

function LinkRestaurantDialog({
  profile,
  programState,
  demoMode,
  onStateChange,
  onClose,
}) {
  const [form, setForm] = useState({
    companyName: "",
    branchName: "Casa central",
    restaurantEmail: "",
    businessType: "Restaurante",
    locationType: "restaurant",
  });
  const [createdLocation, setCreatedLocation] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      if (demoMode) {
        const result = linkLocalRestaurant(
          programState,
          profile,
          form,
          window.location.origin
        );
        await onStateChange(result.state);
        setCreatedLocation(result.location);
      } else {
        const location = await createCollaboratorLocationReferral(
          { ...form, sellerCode: profile.sellerCode },
          window.location.origin
        );
        await onStateChange(null);
        setCreatedLocation(location);
      }
    } catch (submitError) {
      setError(submitError.message || "No pudimos vincular el comercio.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={createdLocation ? "Comercio pre-vinculado" : "Vincular comercio"}
      description={
        createdLocation
          ? "El correo quedo asociado a tu codigo antes del registro."
          : "Carga el correo que usara el comercio para crear su cuenta MozzoApp."
      }
      onClose={onClose}
    >
      {createdLocation ? (
        <div className="collab-result">
          <StatusBadge meta={LOCATION_STATUS_META.prelinked} />
          <h3>{createdLocation.companyName}</h3>
          <p>{createdLocation.restaurantEmail}</p>
          <label className="collab-field">
            <span>Enlace de registro</span>
            <textarea value={createdLocation.onboardingUrl} readOnly rows={3} />
          </label>
          <div className="collab-form-actions">
            <button type="button" className="collab-button collab-button--quiet" onClick={onClose}>Cerrar</button>
            <CopyButton value={createdLocation.onboardingUrl}>Copiar enlace</CopyButton>
          </div>
        </div>
      ) : (
        <form className="collab-form" onSubmit={handleSubmit}>
          <div className="collab-form-grid">
            <label className="collab-field collab-field--wide">
              <span>Nombre comercial</span>
              <input
                value={form.companyName}
                onChange={(event) => setForm((current) => ({ ...current, companyName: event.target.value }))}
                placeholder="Nombre del comercio"
              />
            </label>
            <label className="collab-field collab-field--wide">
              <span>Correo de registro</span>
              <input
                type="email"
                value={form.restaurantEmail}
                onChange={(event) => setForm((current) => ({ ...current, restaurantEmail: event.target.value }))}
                placeholder="administracion@comercio.com"
              />
            </label>
            <label className="collab-field">
              <span>Tipo de alta</span>
              <select
                value={form.locationType}
                onChange={(event) => setForm((current) => ({
                  ...current,
                  locationType: event.target.value,
                  branchName: event.target.value === "branch" ? "Nueva sucursal" : "Casa central",
                }))}
              >
                <option value="restaurant">Comercio principal</option>
                <option value="branch">Sucursal adicional</option>
              </select>
            </label>
            <label className="collab-field">
              <span>Modalidad</span>
              <select
                value={form.businessType}
                onChange={(event) => setForm((current) => ({ ...current, businessType: event.target.value }))}
              >
                <option>Restaurante</option>
                <option>Fast food</option>
                <option>Delivery</option>
                <option>Cafeteria</option>
                <option>Autoservicio</option>
              </select>
            </label>
            <label className="collab-field collab-field--wide">
              <span>Nombre de la sucursal</span>
              <input
                value={form.branchName}
                onChange={(event) => setForm((current) => ({ ...current, branchName: event.target.value }))}
              />
            </label>
            <label className="collab-field collab-field--wide">
              <span>Codigo del vendedor</span>
              <input value={profile.sellerCode} readOnly />
            </label>
          </div>
          {error ? <div className="collab-message collab-message--error">{error}</div> : null}
          <div className="collab-form-actions">
            <button type="button" className="collab-button collab-button--quiet" onClick={onClose}>Cancelar</button>
            <button type="submit" className="collab-button collab-button--primary" disabled={submitting}>
              {submitting ? "Vinculando..." : "Generar vinculacion"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}

function LocationsView({ profile, programState, demoMode, onStateChange }) {
  const location = useLocation();
  const [search, setSearch] = useState("");
  const [linkDialogOpen, setLinkDialogOpen] = useState(Boolean(location.state?.openLink));
  const locations = programState.locations.filter(
    (candidate) => candidate.ownerProfileId === profile.id
  );
  const filteredLocations = locations.filter((candidate) =>
    `${candidate.companyName} ${candidate.branchName} ${candidate.restaurantEmail}`
      .toLowerCase()
      .includes(search.trim().toLowerCase())
  );
  const totalSales = locations.reduce(
    (sum, candidate) => sum + Number(candidate.commissionableSales || 0),
    0
  );
  const totalCommission = locations.reduce(
    (sum, candidate) => sum + Number(candidate.collaboratorCommission || 0),
    0
  );

  return (
    <>
      <div className="collab-page-heading">
        <div>
          <span className="collab-kicker">Cartera personal</span>
          <h1>Comercios vinculados</h1>
          <p>Seguimiento por sucursal del ciclo mensual y su condicion operativa.</p>
        </div>
        <button type="button" className="collab-button collab-button--primary" onClick={() => setLinkDialogOpen(true)}>Vincular comercio</button>
      </div>

      <MetricStrip
        metrics={[
          { label: "Vinculadas", value: String(locations.length), detail: "Sucursales personales" },
          { label: "Operativas", value: String(locations.filter((candidate) => isOperationalCollaboratorLocation(candidate, programState.programSettings)).length), detail: "Cuentan para objetivos" },
          { label: "Venta comisionable", value: formatMoney(totalSales), detail: "Neto del ciclo" },
          { label: "Comision directa", value: formatMoney(totalCommission), detail: "Antes de ajustes" },
        ]}
      />

      <section className="collab-surface collab-surface--table">
        <div className="collab-table-toolbar">
          <label className="collab-search">
            <span>Buscar</span>
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Nombre, sucursal o correo" />
          </label>
          <span className="collab-table-count">{filteredLocations.length} resultados</span>
        </div>
        <LocationTable
          locations={filteredLocations}
          settings={programState.programSettings}
        />
      </section>

      {linkDialogOpen ? (
        <LinkRestaurantDialog
          profile={profile}
          programState={programState}
          demoMode={demoMode}
          onStateChange={onStateChange}
          onClose={() => setLinkDialogOpen(false)}
        />
      ) : null}
    </>
  );
}

function InviteCollaboratorDialog({ profile, programState, onStateChange, onClose }) {
  const [form, setForm] = useState({ name: "", email: "" });
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const demoMode = isCollaboratorProgramDemoMode();

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      if (demoMode) {
        const result = createLocalCollaboratorInvitation(
          programState,
          profile,
          form,
          window.location.origin
        );
        await onStateChange(result.state);
        setInvitation(result.invitation);
        return;
      }

      const result = await createCollaboratorAccessInvitation({
        role: "normal",
        name: form.name,
        email: form.email,
        origin: window.location.origin,
      });
      const nextState = mergeCollaboratorAccessState(programState, {
        invitations: [result.invitation],
      });
      await onStateChange(nextState);
      setInvitation({
        ...result.invitation,
        deliverySent: result.delivery.sent,
        deliveryMessage: result.delivery.message,
      });
    } catch (submitError) {
      setError(submitError.message || "No pudimos crear la invitacion.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={invitation ? "Invitacion creada" : "Invitar colaborador"}
      description="El alta queda asociada a tu red y solo puede utilizarse con el correo indicado."
      onClose={onClose}
    >
      {invitation ? (
        <div className="collab-result">
          <StatusBadge meta={{ label: "Pendiente", tone: "warning" }} />
          <h3>{invitation.name}</h3>
          <p>{invitation.email}</p>
          <div
            className={`collab-message ${
              invitation.deliverySent === false
                ? "collab-message--error"
                : "collab-message--success"
            }`}
          >
            {invitation.deliveryMessage ||
              (demoMode
                ? "Invitacion local creada."
                : "El correo de acceso fue enviado.")}
          </div>
          <div className="collab-code-box">
            <span>Codigo</span>
            <strong>{invitation.code}</strong>
          </div>
          <label className="collab-field">
            <span>Enlace de alta</span>
            <textarea value={invitation.registrationUrl} readOnly rows={3} />
          </label>
          <div className="collab-form-actions">
            <button type="button" className="collab-button collab-button--quiet" onClick={onClose}>Cerrar</button>
            <CopyButton value={invitation.registrationUrl}>Copiar enlace</CopyButton>
          </div>
        </div>
      ) : (
        <form className="collab-form" onSubmit={handleSubmit}>
          <label className="collab-field">
            <span>Nombre y apellido</span>
            <input value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} />
          </label>
          <label className="collab-field">
            <span>Correo</span>
            <input type="email" value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} />
          </label>
          {error ? <div className="collab-message collab-message--error">{error}</div> : null}
          <div className="collab-form-actions">
            <button type="button" className="collab-button collab-button--quiet" onClick={onClose}>Cancelar</button>
            <button
              type="submit"
              className="collab-button collab-button--primary"
              disabled={submitting}
            >
              {submitting ? "Creando y enviando..." : "Crear y enviar invitacion"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}

function NetworkView({ profile, programState, onStateChange }) {
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const memberPromotionTarget = COLLABORATOR_PROGRAM_RULES.normalPromotionLocationTarget;
  const members = programState.networkMembers.filter(
    (member) => member.starProfileId === profile.id
  );
  const activeMembers = members.filter((member) => member.status === "active");
  const networkActiveLocationCount = activeMembers.reduce(
    (sum, member) => sum + Number(member.activeLocationCount || 0),
    0
  );
  const ownOperationalLocationCount = programState.locations.filter(
    (location) =>
      location.ownerProfileId === profile.id &&
      isOperationalCollaboratorLocation(location, programState.programSettings)
  ).length;
  const bonusPortfolio = resolveStarBonusPortfolio({
    profile,
    networkOperationalLocationCount: networkActiveLocationCount,
    ownOperationalLocationCount,
  });
  const activeLocationCount = bonusPortfolio.activeLocationCount;
  const bonusStatus = resolveStarNetworkBonus({
    activeLocationCount,
    ...(programState.starNetworkStatus[profile.id] || {}),
  });
  const pendingInvitations = programState.invitations.filter(
    (invitation) => invitation.starProfileId === profile.id && invitation.status === "pending"
  );

  return (
    <>
      <div className="collab-page-heading">
        <div>
          <span className="collab-kicker">Colaborador estrella</span>
          <h1>Mi red comercial</h1>
          <p>Colaboradores directos, sucursales operativas y bono vigente.</p>
        </div>
        <button type="button" className="collab-button collab-button--primary" onClick={() => setInviteDialogOpen(true)}>Invitar colaborador</button>
      </div>

      <MetricStrip
        metrics={[
          { label: "Colaboradores activos", value: String(activeMembers.length), detail: `${pendingInvitations.length} invitaciones pendientes` },
          {
            label: "Sucursales para el bono",
            value: String(activeLocationCount),
            detail: bonusPortfolio.includesOwnLocations
              ? `${networkActiveLocationCount} de red + ${ownOperationalLocationCount} propias`
              : "Solo las incorporadas por la red",
          },
          { label: "Bono vigente", value: formatProgramRate(bonusStatus.rate), detail: bonusStatus.lockedUntilTarget ? "Requiere volver a 100" : "Sobre ganancia MozzoApp" },
          { label: "Proximo objetivo", value: `${activeLocationCount}/100`, detail: `${Math.max(0, 100 - activeLocationCount)} sucursales restantes` },
        ]}
      />

      <section className="collab-surface collab-objective">
        <div className="collab-objective__header">
          <div>
            <span className="collab-section-label">Escala del bono</span>
            <h2>{formatProgramRate(bonusStatus.rate)} sobre la ganancia de la matriz</h2>
          </div>
          <div className="collab-objective__rate">
            <span>Red operativa</span>
            <strong>{activeLocationCount}</strong>
          </div>
        </div>
        <ProgressBar value={activeLocationCount} max={100} label={`${activeLocationCount} sucursales operativas`} />
        <div className="collab-bonus-scale" aria-label="Escala entre 90 y 100 sucursales">
          {[90, 92, 94, 96, 98, 100].map((count) => (
            <span key={count} className={count === activeLocationCount ? "is-current" : ""}>{count}</span>
          ))}
        </div>
      </section>

      <section className="collab-surface collab-surface--table">
        <div className="collab-section-heading collab-section-heading--padded">
          <div>
            <span className="collab-section-label">Matriz directa</span>
            <h2>Colaboradores de la red</h2>
          </div>
        </div>
        <div className="collab-table-wrap">
          <table className="collab-table">
            <thead><tr><th>Colaborador</th><th>Codigo</th><th>Sucursales operativas</th><th>Progreso individual</th><th>Estado</th></tr></thead>
            <tbody>
              {members.map((member) => (
                <tr key={member.id}>
                  <td><strong>{member.name}</strong><small>{member.email}</small></td>
                  <td className="collab-mono">{member.sellerCode}</td>
                  <td>{member.activeLocationCount}</td>
                  <td>
                    <div className="collab-inline-progress">
                      <ProgressBar
                        value={member.activeLocationCount}
                        max={memberPromotionTarget}
                        label={`${member.activeLocationCount} de ${memberPromotionTarget}`}
                      />
                      <span>
                        {member.activeLocationCount}/{memberPromotionTarget}
                      </span>
                    </div>
                  </td>
                  <td><StatusBadge meta={member.status === "active" ? { label: "Activo", tone: "success" } : { label: "Validacion", tone: "warning" }} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {pendingInvitations.length ? (
        <section className="collab-surface collab-surface--table">
          <div className="collab-section-heading collab-section-heading--padded">
            <div><span className="collab-section-label">Altas abiertas</span><h2>Invitaciones pendientes</h2></div>
          </div>
          <div className="collab-table-wrap">
            <table className="collab-table collab-table--compact">
              <thead><tr><th>Persona</th><th>Codigo</th><th>Fecha</th><th>Estado</th></tr></thead>
              <tbody>{pendingInvitations.map((invitation) => (
                <tr key={invitation.id}>
                  <td><strong>{invitation.name}</strong><small>{invitation.email}</small></td>
                  <td className="collab-mono">{invitation.code}</td>
                  <td>{formatDate(invitation.createdAt)}</td>
                  <td><StatusBadge meta={{ label: "Pendiente", tone: "warning" }} /></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </section>
      ) : null}

      {inviteDialogOpen ? (
        <InviteCollaboratorDialog profile={profile} programState={programState} onStateChange={onStateChange} onClose={() => setInviteDialogOpen(false)} />
      ) : null}
    </>
  );
}

function InvoiceDialog({
  profile,
  programState,
  settlement,
  demoMode,
  onStateChange,
  onClose,
}) {
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!invoiceFile) {
      setError("Selecciona el archivo PDF o imagen de la factura.");
      return;
    }

    setSubmitting(true);
    try {
      if (demoMode) {
        const nextState = registerLocalSettlementInvoice(programState, profile, {
          settlementId: settlement.id,
          invoiceNumber,
          invoiceFileName: invoiceFile.name,
        });
        await onStateChange(nextState);
      } else {
        await registerCollaboratorSettlementInvoice({
          profileId: profile.id,
          settlementId: settlement.id,
          invoiceNumber,
          file: invoiceFile,
        });
        await onStateChange(null);
      }
      onClose();
    } catch (submitError) {
      setError(submitError.message || "No pudimos registrar la factura.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title="Presentar factura" description={`Liquidacion ${settlement.cycleLabel} por ${formatMoney(settlement.netAmount)}.`} onClose={onClose}>
      <form className="collab-form" onSubmit={handleSubmit}>
        <label className="collab-field">
          <span>Numero de comprobante</span>
          <input value={invoiceNumber} onChange={(event) => setInvoiceNumber(event.target.value)} placeholder="C 00002-00000123" required />
        </label>
        <label className="collab-file-field">
          <span>Archivo de factura</span>
          <input type="file" accept="application/pdf,image/png,image/jpeg" onChange={(event) => setInvoiceFile(event.target.files?.[0] || null)} />
          <strong>{invoiceFile?.name || "Seleccionar PDF, PNG o JPG"}</strong>
        </label>
        <div className="collab-invoice-total"><span>Total del comprobante</span><strong>{formatMoney(settlement.netAmount)}</strong></div>
        {error ? <div className="collab-message collab-message--error">{error}</div> : null}
        <div className="collab-form-actions">
          <button type="button" className="collab-button collab-button--quiet" onClick={onClose}>Cancelar</button>
          <button type="submit" className="collab-button collab-button--primary" disabled={submitting}>
            {submitting ? "Subiendo..." : "Presentar factura"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function SettlementsView({ profile, programState, demoMode, onStateChange }) {
  const settlements = programState.settlements.filter(
    (settlement) => settlement.profileId === profile.id
  );
  const awaitingSettlement = settlements.find(
    (settlement) => settlement.status === "awaiting_invoice"
  );
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const totalPaid = settlements
    .filter((settlement) => settlement.status === "paid")
    .reduce((sum, settlement) => sum + Number(settlement.netAmount || 0), 0);

  return (
    <>
      <div className="collab-page-heading">
        <div>
          <span className="collab-kicker">Cierres mensuales</span>
          <h1>Liquidaciones y facturas</h1>
          <p>Cada pago queda respaldado por tu comprobante fiscal y la transferencia al alias validado.</p>
        </div>
        {awaitingSettlement ? (
          <button type="button" className="collab-button collab-button--primary" onClick={() => setSelectedSettlement(awaitingSettlement)}>Presentar factura</button>
        ) : null}
      </div>

      <MetricStrip
        metrics={[
          { label: "Pendiente de facturar", value: formatMoney(awaitingSettlement?.netAmount), detail: awaitingSettlement ? `Vencio ${formatDate(awaitingSettlement.invoiceDueDate)}` : "Sin pendientes" },
          { label: "Cobrado historico", value: formatMoney(totalPaid), detail: "En este prototipo" },
          { label: "Alias de pago", value: profile.payoutAlias || "Sin alias", detail: profile.cuit },
          { label: "Proximo pago", value: awaitingSettlement ? "Siguiente ciclo" : "Sin programar", detail: "Factura fuera de termino" },
        ]}
      />

      <section className="collab-surface collab-surface--table">
        <div className="collab-section-heading collab-section-heading--padded">
          <div><span className="collab-section-label">Historial</span><h2>Liquidaciones emitidas</h2></div>
        </div>
        <div className="collab-table-wrap">
          <table className="collab-table">
            <thead><tr><th>Ciclo</th><th>Venta comisionable</th><th>Ajustes</th><th>Total</th><th>Factura</th><th>Estado</th><th /></tr></thead>
            <tbody>{settlements.map((settlement) => (
              <tr key={settlement.id}>
                <td><strong>{settlement.cycleLabel}</strong><small>Cierre {formatDate(settlement.cycleEnd)}</small></td>
                <td>{formatMoney(settlement.commissionableSales)}</td>
                <td className={settlement.creditNotes < 0 ? "is-negative" : ""}>{formatMoney(settlement.creditNotes)}</td>
                <td><strong>{formatMoney(settlement.netAmount)}</strong></td>
                <td>{settlement.invoiceNumber || "Pendiente"}</td>
                <td><StatusBadge meta={SETTLEMENT_STATUS_META[settlement.status] || { label: settlement.status, tone: "neutral" }} /></td>
                <td>{settlement.status === "awaiting_invoice" ? <button type="button" className="collab-text-button" onClick={() => setSelectedSettlement(settlement)}>Facturar</button> : null}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </section>

      <section className="collab-surface collab-payment-account">
        <div>
          <span className="collab-section-label">Cuenta de cobro</span>
          <h2>{profile.payoutAlias || "Alias pendiente"}</h2>
          <p>La titularidad debe coincidir con {profile.firstName} {profile.lastName}, CUIT {profile.cuit}.</p>
        </div>
        <StatusBadge meta={{ label: "Titular validado", tone: "success" }} />
      </section>

      {selectedSettlement ? (
        <InvoiceDialog profile={profile} programState={programState} settlement={selectedSettlement} demoMode={demoMode} onStateChange={onStateChange} onClose={() => setSelectedSettlement(null)} />
      ) : null}
    </>
  );
}

function ProfileView({ profile, programState, demoMode, onStateChange }) {
  const [form, setForm] = useState({
    phone: profile.phone || "",
    payoutAlias: profile.payoutAlias || "",
  });
  const [message, setMessage] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("");
    try {
      if (demoMode) {
        await onStateChange(
          updateLocalCollaboratorProfile(programState, profile.id, form)
        );
      } else {
        await updateCollaboratorAccessProfile(profile.id, form);
        await onStateChange(null);
      }
      setMessage("Datos actualizados correctamente.");
    } catch (error) {
      setMessage(error.message || "No pudimos actualizar tus datos.");
    }
  };

  return (
    <>
      <div className="collab-page-heading">
        <div>
          <span className="collab-kicker">Identidad fiscal</span>
          <h1>Mi perfil</h1>
          <p>Datos personales, condicion tributaria y cuenta utilizada para recibir liquidaciones.</p>
        </div>
      </div>

      <div className="collab-two-columns collab-two-columns--profile">
        <section className="collab-surface">
          <div className="collab-section-heading">
            <div><span className="collab-section-label">Datos del colaborador</span><h2>{profile.firstName} {profile.lastName}</h2></div>
            <StatusBadge meta={{ label: profile.role === "star" ? "Estrella" : "Colaborador", tone: profile.role === "star" ? "star" : "info" }} />
          </div>
          <form className="collab-form" onSubmit={handleSubmit}>
            <div className="collab-form-grid">
              <label className="collab-field collab-field--wide"><span>Correo</span><input value={profile.email} readOnly /></label>
              <label className="collab-field"><span>CUIT</span><input value={profile.cuit} readOnly /></label>
              <label className="collab-field"><span>Codigo de vendedor</span><input value={profile.sellerCode} readOnly /></label>
              <label className="collab-field"><span>Telefono</span><input value={form.phone} onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))} /></label>
              <label className="collab-field"><span>Alias Mercado Pago</span><input value={form.payoutAlias} onChange={(event) => setForm((current) => ({ ...current, payoutAlias: event.target.value }))} /></label>
            </div>
            {message ? <div className="collab-message collab-message--success">{message}</div> : null}
            <div className="collab-form-actions"><button type="submit" className="collab-button collab-button--primary">Guardar cambios</button></div>
          </form>
        </section>

        <aside className="collab-surface">
          <div className="collab-section-heading"><div><span className="collab-section-label">Situacion tributaria</span><h2>{profile.taxStatus}</h2></div><StatusBadge meta={{ label: "Vigente", tone: "success" }} /></div>
          <dl className="collab-profile-facts">
            <div><dt>Factura requerida</dt><dd>Comprobante fiscal valido</dd></div>
            <div><dt>Fecha limite</dt><dd>Dia 10 de cada mes</dd></div>
            <div><dt>Transferencia</dt><dd>Hasta el dia 15</dd></div>
            <div><dt>Titular de cuenta</dt><dd>Debe coincidir con el CUIT</dd></div>
          </dl>
        </aside>
      </div>
    </>
  );
}

function CollaboratorWorkspace({
  profile,
  programState,
  launcherConfig,
  demoMode,
  onStateChange,
  onLogout,
  onReset,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const routeKey = location.pathname.split("/")[2] || "panel";
  const isStar = profile.role === "star";
  const navItems = [
    { key: "panel", label: "Resumen", path: "/colaboradores/panel" },
    { key: "comercios", label: "Comercios", path: "/colaboradores/comercios" },
    ...(isStar ? [{ key: "red", label: "Mi red", path: "/colaboradores/red" }] : []),
    { key: "liquidaciones", label: "Liquidaciones", path: "/colaboradores/liquidaciones" },
    { key: "perfil", label: "Perfil fiscal", path: "/colaboradores/perfil" },
  ];
  const pageTitle = navItems.find((item) => item.key === routeKey)?.label || "Resumen";

  let content = null;
  if (routeKey === "comercios") {
    content = <LocationsView profile={profile} programState={programState} demoMode={demoMode} onStateChange={onStateChange} />;
  } else if (routeKey === "red" && isStar) {
    content = <NetworkView profile={profile} programState={programState} onStateChange={onStateChange} />;
  } else if (routeKey === "liquidaciones") {
    content = <SettlementsView profile={profile} programState={programState} demoMode={demoMode} onStateChange={onStateChange} />;
  } else if (routeKey === "perfil") {
    content = <ProfileView profile={profile} programState={programState} demoMode={demoMode} onStateChange={onStateChange} />;
  } else {
    content = <DashboardView profile={profile} programState={programState} navigate={navigate} />;
  }

  return (
    <div className="collab-workspace">
      <aside className="collab-sidebar">
        <Link className="collab-brand collab-brand--sidebar" to="/colaboradores/panel">
          <img src={MOZZOAPP_INSTITUTIONAL_LOGO_URL} alt="" />
          <span><strong>MozzoApp</strong><small>Colaboradores</small></span>
        </Link>
        <nav className="collab-nav" aria-label="Portal de colaboradores">
          {navItems.map((item) => (
            <NavLink key={item.key} to={item.path} className={({ isActive }) => isActive ? "is-active" : ""}>{item.label}</NavLink>
          ))}
        </nav>
        <div className="collab-sidebar__footer">
          <span>{demoMode ? "Vista preliminar" : "Produccion"}</span>
          {demoMode ? (
            <button type="button" onClick={onReset}>Reiniciar datos demo</button>
          ) : null}
          <Link to="/login">Volver a MozzoApp</Link>
        </div>
      </aside>

      <div className="collab-main">
        <header className="collab-topbar">
          <div><span>Portal comercial</span><strong>{pageTitle}</strong></div>
          <div className="collab-user-menu">
            <CollaboratorLauncherControl launcherConfig={launcherConfig} />
            <span className="collab-user-avatar">{profile.firstName.slice(0, 1)}{profile.lastName.slice(0, 1)}</span>
            <span className="collab-user-copy"><strong>{profile.firstName} {profile.lastName}</strong><small>{isStar ? "Colaborador estrella" : "Colaborador"}</small></span>
            <button type="button" className="collab-button collab-button--quiet" onClick={onLogout}>Salir</button>
          </div>
        </header>

        <nav className="collab-mobile-nav" aria-label="Portal de colaboradores">
          {navItems.map((item) => (
            <NavLink key={item.key} to={item.path} className={({ isActive }) => isActive ? "is-active" : ""}>{item.label}</NavLink>
          ))}
        </nav>

        <main className="collab-content">
          {programState.refreshWarning ? (
            <div className="collab-message" role="status">{programState.refreshWarning}</div>
          ) : null}
          {content}
        </main>
      </div>
    </div>
  );
}

function Colaboradores() {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  const demoMode = isCollaboratorProgramDemoMode();
  const [programState, setProgramState] = useState(() =>
    demoMode
      ? loadLocalCollaboratorProgramState()
      : createEmptyCollaboratorProgramState()
  );
  const [session, setSession] = useState(loadLocalCollaboratorSession);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessCheckedUserId, setAccessCheckedUserId] = useState(null);
  const [accessError, setAccessError] = useState("");
  const [accessAttempt, setAccessAttempt] = useState(0);
  const [accessWaitExpired, setAccessWaitExpired] = useState(false);
  const registrationMode = location.pathname === "/colaboradores/registro";
  const launcherConfig = useCollaboratorLauncherConfig();
  const sessionProfile = session
    ? getCollaboratorProfile(programState, session.profileId)
    : null;
  const authenticatedEmail = String(auth.user?.email || "").trim().toLowerCase();
  const profile =
    sessionProfile &&
    (demoMode ||
      (authenticatedEmail && sessionProfile.email === authenticatedEmail &&
        sessionProfile.userId === auth.user?.id))
      ? sessionProfile
      : null;
  const hasAcceptedContract = profile
    ? hasAcceptedCurrentCollaboratorContract(profile)
    : false;

  useEffect(() => {
    setAccessWaitExpired(false);
    if (demoMode || (!auth.loadingAuth && !accessLoading)) return undefined;
    const timer = window.setTimeout(() => setAccessWaitExpired(true), 15000);
    return () => window.clearTimeout(timer);
  }, [demoMode, auth.loadingAuth, accessLoading, accessAttempt]);

  const reloadRemoteProgram = useCallback(async () => {
    const nextState = await loadCollaboratorProgramState({
      origin: window.location.origin,
    });
    setProgramState(nextState);
    return nextState;
  }, []);

  const handleProgramStateChange = useCallback(
    async (nextState) => {
      if (demoMode) {
        if (nextState) {
          setProgramState(nextState);
        }
        return nextState;
      }

      return reloadRemoteProgram();
    },
    [demoMode, reloadRemoteProgram]
  );

  useEffect(() => {
    document.title = "MozzoApp | Colaboradores";
    return () => {
      document.title = "MozzoApp";
    };
  }, []);

  useEffect(() => {
    if (!demoMode) {
      return undefined;
    }

    const refreshProgramState = (event) => {
      if (
        event?.type === "storage" &&
        event.key !== COLLABORATOR_PROGRAM_STORAGE_KEY
      ) {
        return;
      }

      setProgramState(
        event?.detail && typeof event.detail === "object"
          ? event.detail
          : loadLocalCollaboratorProgramState()
      );
    };

    window.addEventListener("storage", refreshProgramState);
    window.addEventListener(
      COLLABORATOR_PROGRAM_CHANGED_EVENT,
      refreshProgramState
    );
    return () => {
      window.removeEventListener("storage", refreshProgramState);
      window.removeEventListener(
        COLLABORATOR_PROGRAM_CHANGED_EVENT,
        refreshProgramState
      );
    };
  }, [demoMode]);

  useEffect(() => {
    if (demoMode || auth.loadingAuth) {
      return undefined;
    }

    if (!auth.user?.id || !authenticatedEmail) {
      clearLocalCollaboratorSession();
      setSession(null);
      setAccessLoading(false);
      setAccessCheckedUserId(null);
      setAccessError("");
      return undefined;
    }

    let active = true;
    setAccessLoading(true);
    setAccessError("");

    const loadRemoteAccess = async () => {
      try {
        let nextState = await loadCollaboratorProgramState({
          origin: window.location.origin,
        });
        if (!active) {
          return;
        }

        let matchingProfile = findCollaboratorProfileForUser(
          nextState.profiles,
          authenticatedEmail,
          auth.user.id
        );

        if (!matchingProfile && !registrationMode) {
          const resumedAccess = await resumeCurrentCollaboratorAccess({
            email: authenticatedEmail,
            userId: auth.user.id,
            origin: window.location.origin,
          });

          if (resumedAccess?.profile) {
            nextState = await loadCollaboratorProgramState({
              origin: window.location.origin,
            });
            matchingProfile = findCollaboratorProfileForUser(
              nextState.profiles,
              authenticatedEmail,
              auth.user.id
            );
          }
        }

        if (!active) {
          return;
        }

        setProgramState(nextState);

        if (matchingProfile) {
          const nextSession = createLocalDemoSession(
            nextState,
            matchingProfile.role,
            matchingProfile.email
          );
          setSession(nextSession);
        } else {
          clearLocalCollaboratorSession();
          setSession(null);
        }
      } catch (error) {
        if (active) {
          setAccessError(
            error.message || "No pudimos cargar el acceso de colaborador."
          );
        }
      } finally {
        if (active) {
          setAccessCheckedUserId(auth.user.id);
          setAccessLoading(false);
        }
      }
    };

    loadRemoteAccess();
    return () => {
      active = false;
    };
  }, [
    auth.loadingAuth,
    auth.user?.id,
    authenticatedEmail,
    demoMode,
    registrationMode,
    accessAttempt,
  ]);

  const handleLogin = async ({ email, password }) => {
    const normalizedEmail = email.trim().toLowerCase();
    setAccessError("");
    setAccessWaitExpired(false);

    if (!demoMode) {
      await auth.signIn({ email: normalizedEmail, password });
      navigate("/colaboradores/panel", { replace: true });
      return;
    }

    const matchingProfile = programState.profiles.find(
      (candidate) => candidate.email === normalizedEmail
    );

    if (!matchingProfile) {
      throw new Error("No existe un colaborador local con ese correo.");
    }

    const nextSession = createLocalDemoSession(
      programState,
      matchingProfile.role,
      normalizedEmail
    );
    setSession(nextSession);
    navigate("/colaboradores/panel", { replace: true });
  };

  const handleOpenDemo = (role) => {
    const nextSession = createLocalDemoSession(programState, role);
    setSession(nextSession);
    navigate("/colaboradores/panel", { replace: true });
  };

  const handleRegister = async (values) => {
    if (!demoMode) {
      const invitedEmail = values.email.trim().toLowerCase();
      if (!auth.user?.id || authenticatedEmail !== invitedEmail) {
        throw new Error(
          "Abre el enlace recibido por correo en este mismo navegador antes de activar el perfil."
        );
      }

      await updateCurrentUserPassword({ password: values.password });
      let result;

      try {
        result = await acceptCollaboratorAccessInvitation(values);
      } catch (activationError) {
        result = await resumeCurrentCollaboratorAccess({
          email: invitedEmail,
          userId: auth.user.id,
          origin: window.location.origin,
          values,
        });

        if (!result?.profile) {
          throw activationError;
        }
      }

      const nextState = await reloadRemoteProgram();
      setProgramState(nextState);
      const nextSession = createLocalDemoSession(
        nextState,
        result.profile.role,
        result.profile.email
      );
      setSession(nextSession);
      navigate("/colaboradores/panel", { replace: true });
      return;
    }

    const result = registerLocalCollaborator(programState, values);
    setProgramState(result.state);
    setSession(result.session);
    navigate("/colaboradores/panel", { replace: true });
  };

  const handleContractAccept = async (nextState) => {
    const nextProfile = getCollaboratorProfile(nextState, profile?.id);

    if (!demoMode && nextProfile) {
      await updateCollaboratorAccessProfile(
        nextProfile.id,
        {
          firstName: nextProfile.firstName,
          lastName: nextProfile.lastName,
          phone: nextProfile.phone,
          cuit: nextProfile.cuit,
          taxCondition: nextProfile.taxCondition,
          fiscalAddress: nextProfile.fiscalAddress,
          payoutAlias: nextProfile.payoutAlias,
          contractAcceptance: nextProfile.contractAcceptance,
        }
      );
      await reloadRemoteProgram();
    } else {
      setProgramState(nextState);
    }
    navigate("/colaboradores/panel", { replace: true });
  };

  const handleLogout = async () => {
    clearLocalCollaboratorSession();
    setSession(null);
    if (!demoMode && auth.user) {
      await auth.signOut({ scope: "local" });
    }
    setAccessError("");
    setAccessWaitExpired(false);
    navigate("/colaboradores", { replace: true });
  };

  const handleReset = () => {
    const nextState = resetLocalCollaboratorProgram();
    setProgramState(nextState);
    setSession(null);
    navigate("/colaboradores", { replace: true });
  };

  const accessRoute =
    location.pathname === "/colaboradores" || registrationMode;

  const accessFailure = accessError || (!auth.user?.id && auth.authError) || "";
  const accessMessage = isMissingAuthSessionError(accessFailure)
    ? MISSING_AUTH_SESSION_MESSAGE : accessFailure;
  if (!demoMode && !profile && (accessWaitExpired || (auth.user?.id && accessFailure))) {
    return (
      <main className="collab-access collab-access--loading">
        <section className="collab-access-loader" role="alert">
          <h1>No pudimos completar el acceso</h1>
          <p>{accessMessage || "La conexion esta tardando demasiado. Tus datos de registro siguen guardados."}</p>
          <button type="button" className="collab-button collab-button--primary" onClick={() => {
            if (auth.loadingAuth || auth.authError) {
              window.location.reload();
            } else {
              setAccessError("");
              setAccessWaitExpired(false);
              setAccessAttempt((attempt) => attempt + 1);
            }
          }}>Reintentar acceso</button>
          {!auth.loadingAuth ? (
            <button type="button" className="collab-button collab-button--quiet" onClick={async () => {
              try { await handleLogout(); }
              catch (error) { setAccessError(error.message || "No pudimos reiniciar el acceso. Vuelve a intentar."); }
            }}>Volver a iniciar sesion</button>
          ) : null}
        </section>
      </main>
    );
  }

  if (!demoMode && (auth.loadingAuth || (accessLoading && !profile) ||
      (auth.user?.id && accessCheckedUserId !== auth.user.id))) {
    return (
      <main className="collab-access collab-access--loading">
        <div className="collab-access-loader">Verificando acceso...</div>
      </main>
    );
  }

  if (!profile) {
    if (!accessRoute) {
      return <Navigate to="/colaboradores" replace />;
    }

    return (
      <CollaboratorAccess
        mode={registrationMode ? "register" : "login"}
        programState={programState}
        launcherConfig={launcherConfig}
        demoMode={demoMode}
        externalError={accessMessage}
        onLogin={handleLogin}
        onOpenDemo={handleOpenDemo}
        onRegister={handleRegister}
      />
    );
  }

  if (!hasAcceptedContract) {
    return (
      <CollaboratorContractGate
        profile={profile}
        programState={programState}
        onAccept={handleContractAccept}
        onLogout={handleLogout}
      />
    );
  }

  if (accessRoute) {
    return <Navigate to="/colaboradores/panel" replace />;
  }

  return (
    <CollaboratorWorkspace
      profile={profile}
      programState={programState}
      launcherConfig={launcherConfig}
      demoMode={demoMode}
      onStateChange={handleProgramStateChange}
      onLogout={handleLogout}
      onReset={handleReset}
    />
  );
}

export default Colaboradores;
